import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  RefreshCw, Play, Loader2, CheckCircle2, AlertTriangle, Clock, Calendar,
  Database, Info, ChevronRight, Gauge, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { api, apiErrorMessage } from "@/lib/api";

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtDuration(startIso, endIso) {
  if (!startIso || !endIso) return "—";
  try {
    const ms = new Date(endIso) - new Date(startIso);
    const s = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(s / 60);
    const h = Math.floor(m / 60);
    if (h > 0) return `${h}h ${m % 60}m`;
    if (m > 0) return `${m}m ${s % 60}s`;
    return `${s}s`;
  } catch { return "—"; }
}

const STATUS_COLOR = {
  running: "bg-blue-100 text-blue-800 border-blue-300",
  done: "bg-emerald-100 text-emerald-800 border-emerald-300",
  done_with_errors: "bg-amber-100 text-amber-800 border-amber-300",
  error: "bg-rose-100 text-rose-800 border-rose-300",
  aborted: "bg-slate-100 text-slate-700 border-slate-300",
  partial: "bg-violet-100 text-violet-800 border-violet-300",
  skipped: "bg-slate-100 text-slate-600 border-slate-300",
};
const STATUS_LABEL = {
  running: "en curso", done: "completado", done_with_errors: "completado con errores",
  error: "error", aborted: "abortado (reinicio)", partial: "parcial (sin tiempo)", skipped: "omitido",
};
const TRIGGER_LABEL = { cron: "programado", auto_login: "auto (login)", manual: "manual" };

function CoverageCard({ cov }) {
  if (!cov) return null;
  const pct = (n) => (cov.total ? Math.round((n / cov.total) * 100) : 0);
  const tone = pct(cov.fresh_7d) >= 90 ? "text-emerald-700" : pct(cov.fresh_7d) >= 60 ? "text-amber-700" : "text-rose-700";
  return (
    <Card data-testid="sofi1-sync-card-coverage">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><Gauge className="h-4 w-4 text-primary" /> Cobertura · ¿están al día los {cov.total} viajes de {cov.year}?</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded border p-3"><div className={`text-2xl font-bold ${tone}`} data-testid="sofi1-cov-fresh7">{cov.fresh_7d}<span className="text-sm font-normal text-muted-foreground"> / {cov.total}</span></div><div className="text-xs text-muted-foreground">refrescados en 7 días ({pct(cov.fresh_7d)} %)</div></div>
          <div className="rounded border p-3"><div className="text-2xl font-bold" data-testid="sofi1-cov-fresh24">{cov.fresh_24h}</div><div className="text-xs text-muted-foreground">refrescados en 24 h</div></div>
          <div className="rounded border p-3"><div className="text-2xl font-bold text-rose-700" data-testid="sofi1-cov-stale">{cov.stale_7d}</div><div className="text-xs text-muted-foreground">más de 7 días sin refrescar</div></div>
          <div className="rounded border p-3"><div className="text-2xl font-bold text-slate-600" data-testid="sofi1-cov-never">{cov.never}</div><div className="text-xs text-muted-foreground">sin marca de sync</div></div>
        </div>
        <Progress value={pct(cov.fresh_7d)} />
        {cov.oldest?.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Los más desactualizados (se procesan primero en el próximo sync)</div>
            <div className="divide-y rounded border text-sm" data-testid="sofi1-cov-oldest">
              {cov.oldest.slice(0, 8).map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-3 px-3 py-1.5">
                  <span className="truncate">{t.main_traveler} <span className="text-xs text-muted-foreground">· {t.start_date} · {t.status}</span></span>
                  <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">{t.synced_at ? fmtDT(t.synced_at) : "nunca"}
                    {t.external_url && <a href={t.external_url} target="_blank" rel="noopener noreferrer" aria-label="Abrir en SOFI 1.0"><ExternalLink className="h-3.5 w-3.5" /></a>}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Sofi1SyncPage() {
  const qc = useQueryClient();
  const [tempHour, setTempHour] = useState(null);

  const statusQ = useQuery({
    queryKey: ["sofi1-sync-status"],
    queryFn: async () => (await api.get("/sofi1-sync/status")).data,
    refetchInterval: (data) => (data?.active_run?.status === "running" ? 5000 : 30000),
  });

  const runsQ = useQuery({
    queryKey: ["sofi1-sync-runs"],
    queryFn: async () => (await api.get("/sofi1-sync/runs?limit=20")).data,
    refetchInterval: 30000,
  });

  const covQ = useQuery({
    queryKey: ["sofi1-sync-coverage"],
    queryFn: async () => (await api.get("/sofi1-sync/coverage?limit=20")).data,
    refetchInterval: 60000,
  });

  const configMut = useMutation({
    mutationFn: async (patch) => (await api.post("/sofi1-sync/config", patch)).data,
    onSuccess: () => {
      toast.success("Configuración guardada");
      qc.invalidateQueries({ queryKey: ["sofi1-sync-status"] });
      setTempHour(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const runNowMut = useMutation({
    mutationFn: async () => (await api.post("/sofi1-sync/run-now")).data,
    onSuccess: (data) => {
      if (data.queued) {
        toast.success("Sync manual encolado. Se ejecuta en segundo plano.");
        qc.invalidateQueries({ queryKey: ["sofi1-sync-status"] });
      } else {
        toast.warning(data.reason || "Ya hay un sync en ejecución");
      }
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const cfg = statusQ.data?.config;
  const activeRun = statusQ.data?.active_run;
  const lastRun = statusQ.data?.last_run;
  const runs = runsQ.data?.runs || [];

  if (statusQ.isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-40" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const progressPct = activeRun && activeRun.targets > 0
    ? Math.round(((activeRun.new_count + activeRun.updated_count + activeRun.error_count) / activeRun.targets) * 100)
    : 0;

  return (
    <div className="space-y-5" data-testid="sofi1-sync-page">
      {/* Cabecera */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <RefreshCw className="h-6 w-6 text-primary" />
            Sync diario SOFI 1.0 → SOFI 2.0
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Trae automáticamente los viajes, reservas, contabilidad y pagos nuevos o modificados
            del sistema antiguo (`gestion.viajadverdad.com`) a SOFI 2.0. Idempotente: no duplica.
          </p>
        </div>
      </div>

      {/* Estado + configuración */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card data-testid="sofi1-sync-card-config">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Configuración</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="sync-enabled" className="text-sm">
                Sync automático activo
              </Label>
              <Switch
                id="sync-enabled"
                checked={!!cfg?.enabled}
                onCheckedChange={(v) => configMut.mutate({ enabled: v })}
                data-testid="sofi1-sync-enabled-switch"
                disabled={configMut.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sync-hour" className="text-sm">
                Hora diaria (UTC) — 0=00:00 · 2=02:00 UTC (03:00 CET)
              </Label>
              <div className="flex gap-2">
                <Input
                  id="sync-hour"
                  type="number"
                  min={0}
                  max={23}
                  value={tempHour ?? cfg?.hour_utc ?? 2}
                  onChange={(e) => setTempHour(Number(e.target.value))}
                  className="w-24"
                  data-testid="sofi1-sync-hour-input"
                />
                {tempHour != null && tempHour !== cfg?.hour_utc && (
                  <Button
                    size="sm"
                    onClick={() => configMut.mutate({ hour_utc: tempHour })}
                    data-testid="sofi1-sync-hour-save"
                  >
                    Guardar
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Sesiones en paralelo</Label>
                <Input type="number" min={1} max={4} value={cfg?.workers ?? 2} className="h-8" data-testid="sofi1-sync-workers-input"
                  onChange={(e) => { const v = Number(e.target.value); if (v >= 1 && v <= 4 && v !== cfg?.workers) configMut.mutate({ workers: v }); }} />
              </div>
              <div>
                <Label className="text-xs">Presupuesto (min)</Label>
                <Input type="number" min={5} max={180} step={5} defaultValue={cfg?.max_minutes ?? 50} className="h-8" data-testid="sofi1-sync-budget-input"
                  onBlur={(e) => { const v = Number(e.target.value); if (v >= 5 && v <= 180 && v !== cfg?.max_minutes) configMut.mutate({ max_minutes: v }); }} />
              </div>
            </div>

            <div className="rounded-md bg-muted/50 p-2 text-xs">
              <div className="flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5" />
                <span className="font-medium">Próxima ejecución:</span>
                <span className="text-muted-foreground">{fmtDT(cfg?.next_run_at) || "no programada"}</span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <Clock className="h-3.5 w-3.5" />
                <span className="font-medium">Última ejecución:</span>
                <span className="text-muted-foreground">{fmtDT(cfg?.last_run_at)}</span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <Database className="h-3.5 w-3.5" />
                <span className="font-medium">Año objetivo:</span>
                <span className="text-muted-foreground">{cfg?.year}</span>
              </div>
              <div className="mt-2 border-t pt-2 text-[11px] leading-snug text-muted-foreground">
                <Info className="mr-1 inline h-3 w-3" />
                Con "Sync automático activo", si el pod hiberna y el cron pierde la hora,
                el sync se dispara <strong>en el primer login del día</strong> pasada la hora programada.
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="sofi1-sync-card-active" className="md:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              <span>Estado actual</span>
              <Button
                size="sm"
                onClick={() => runNowMut.mutate()}
                disabled={runNowMut.isPending || !!activeRun}
                data-testid="sofi1-sync-run-now-btn"
              >
                {runNowMut.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Play className="mr-1.5 h-4 w-4" />
                )}
                {activeRun ? "Sync en curso…" : "Ejecutar sync ahora"}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeRun ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  <span className="font-medium">Procesando:</span>
                  <span className="text-muted-foreground">{activeRun.current || "—"}</span>
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      {activeRun.new_count + activeRun.updated_count + activeRun.error_count} / {activeRun.targets}
                    </span>
                    <span className="font-medium">{progressPct}%</span>
                  </div>
                  <Progress value={progressPct} />
                </div>
                <div className="grid grid-cols-3 gap-2 text-center text-sm">
                  <div className="rounded border p-2">
                    <div className="text-lg font-bold text-emerald-700">{activeRun.new_count}</div>
                    <div className="text-xs text-muted-foreground">Nuevos</div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="text-lg font-bold text-blue-700">{activeRun.updated_count}</div>
                    <div className="text-xs text-muted-foreground">Actualizados</div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="text-lg font-bold text-rose-700">{activeRun.error_count}</div>
                    <div className="text-xs text-muted-foreground">Errores</div>
                  </div>
                </div>
              </div>
            ) : lastRun ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  {lastRun.status === "done" ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  ) : lastRun.status === "done_with_errors" ? (
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-rose-600" />
                  )}
                  <div>
                    <div className="font-medium">Último sync: {STATUS_LABEL[lastRun.status] || lastRun.status}{lastRun.remaining > 0 ? ` · ${lastRun.remaining} pendientes` : ""}</div>
                    <div className="text-xs text-muted-foreground">
                      {fmtDT(lastRun.started_at)} · duración {fmtDuration(lastRun.started_at, lastRun.finished_at)}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center text-sm">
                  <div className="rounded border p-2">
                    <div className="text-lg font-bold">{lastRun.targets}</div>
                    <div className="text-xs text-muted-foreground">Objetivo</div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="text-lg font-bold text-emerald-700">{lastRun.new_count}</div>
                    <div className="text-xs text-muted-foreground">Nuevos</div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="text-lg font-bold text-blue-700">{lastRun.updated_count}</div>
                    <div className="text-xs text-muted-foreground">Actualizados</div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="text-lg font-bold text-rose-700">{lastRun.error_count}</div>
                    <div className="text-xs text-muted-foreground">Errores</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                <Info className="mx-auto mb-2 h-5 w-5" />
                Aún no se ha ejecutado ningún sync. Púlsalo cuando quieras o activa el sync
                automático para que corra cada día a la hora configurada.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <CoverageCard cov={covQ.data} />

      {/* Historial */}
      <Card data-testid="sofi1-sync-card-history">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Historial de sincronizaciones</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {runs.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              Sin ejecuciones previas.
            </div>
          ) : (
            <div className="divide-y">
              {runs.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-muted/50"
                  data-testid={`sofi1-sync-run-row-${r.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Badge variant="outline" className={STATUS_COLOR[r.status] || ""}>
                        {STATUS_LABEL[r.status] || r.status}
                      </Badge>
                      <span className="text-muted-foreground">
                        {fmtDT(r.started_at)}
                      </span>
                      <span className="text-xs text-muted-foreground">·</span>
                      <span className="text-xs text-muted-foreground">
                        {TRIGGER_LABEL[r.triggered_by] || r.triggered_by}
                      </span>
                      <span className="text-xs text-muted-foreground">·</span>
                      <span className="text-xs text-muted-foreground">
                        {fmtDuration(r.started_at, r.finished_at)}
                      </span>
                    </div>
                    <div className="mt-1 flex gap-4 text-xs text-muted-foreground">
                      <span>Objetivo: <strong>{r.targets}</strong></span>
                      <span className="text-emerald-700">Nuevos: <strong>{r.new_count}</strong></span>
                      <span className="text-blue-700">Actualizados: <strong>{r.updated_count}</strong></span>
                      {r.error_count > 0 && (
                        <span className="text-rose-700">Errores: <strong>{r.error_count}</strong></span>
                      )}
                      {r.remaining > 0 && r.status !== "running" && (
                        <span className="text-violet-700">Pendientes: <strong>{r.remaining}</strong></span>
                      )}
                      {r.workers > 1 && <span>{r.workers} workers</span>}
                    </div>
                    {r.status === "aborted" && (
                      <div className="mt-1 text-xs text-slate-600" data-testid={`sofi1-sync-abort-${r.id}`}>El servidor se reinició a mitad del sync (memoria agotada o redespliegue). Los viajes pendientes se retoman en el siguiente run.</div>
                    )}
                    {r.status === "error" && r.errors?.[0] && (
                      <div className="mt-1 text-xs text-rose-700" data-testid={`sofi1-sync-err-${r.id}`}>Fallo en «{r.errors[0].stage}» contra SOFI 1.0: {r.errors[0].detail}</div>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="rounded-md border-l-4 border-blue-500 bg-blue-50 p-3 text-sm text-blue-900">
        <div className="flex gap-2">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Cómo funciona.</strong> El sync recorre todos los viajes con llegada en {cfg?.year} en
            los estados <em>{(cfg?.include_statuses || []).join(", ")}</em>. Por cada viaje re-lee del
            SOFI 1.0 la contabilidad, reservas y pagos y hace <strong>upsert</strong> en SOFI 2.0
            (nunca duplica ni borra). Se ejecuta cada día a las <strong>{String(cfg?.hour_utc ?? 2).padStart(2, "0")}:00 UTC</strong>{" "}
            si el sync automático está activado, con <strong>{cfg?.workers ?? 2} sesiones en paralelo</strong> y un
            presupuesto de <strong>{cfg?.max_minutes ?? 50} min</strong> por run: primero los viajes más desactualizados; si no
            da tiempo termina como <em>parcial</em> y el siguiente run (o el próximo login) continúa por los pendientes.
          </div>
        </div>
      </div>
    </div>
  );
}
